import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Plus, FileText, CheckCircle, Clock, Send, AlertCircle,
  Eye, Edit, Trash2, ExternalLink, Download, MessageSquare, Settings, Link2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const requestTypeLabels = {
  creative: 'Creative Design',
  cro: 'CRO Audit',
  ad_audit: 'Ad Audit',
  landing_page: 'Landing Page',
  reporting: 'Reporting',
  social_management: 'Social Management',
  other: 'Other'
};

const statusConfig = {
  draft: { color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300', label: 'Draft' },
  submitted: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', label: 'Submitted' },
  in_progress: { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', label: 'In Progress' },
  review: { color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400', label: 'In Review' },
  completed: { color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400', label: 'Completed' },
  cancelled: { color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400', label: 'Cancelled' }
};

const deliverableStatusConfig = {
  pending: { color: 'bg-slate-100 dark:bg-slate-700 text-slate-600', label: 'Pending' },
  delivered: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700', label: 'Delivered' },
  approved: { color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700', label: 'Approved' },
  revision_requested: { color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700', label: 'Revision Requested' }
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-rose-100 text-rose-600'
};

// Request Card Component
const RequestCard = ({ request, onView, onUpdate }) => {
  const status = statusConfig[request.status] || statusConfig.draft;
  
  return (
    <Card className="dark:bg-slate-800/60 dark:border-slate-700 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-medium dark:text-white">{request.title}</h3>
              <Badge className={status.color}>{status.label}</Badge>
              <Badge className={priorityColors[request.priority]}>{request.priority}</Badge>
            </div>
            <p className="text-sm text-muted-foreground dark:text-slate-400 mt-1">
              {requestTypeLabels[request.request_type] || request.request_type}
            </p>
            <p className="text-sm dark:text-slate-300 mt-2 line-clamp-2">{request.brief}</p>
            {request.due_date && (
              <p className="text-xs text-muted-foreground dark:text-slate-500 mt-2 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Due: {new Date(request.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t dark:border-slate-700">
          <span className="text-xs text-muted-foreground dark:text-slate-400">
            Created {new Date(request.created_at).toLocaleDateString()}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onView(request)}>
              <Eye className="h-4 w-4" />
            </Button>
            {request.status === 'draft' && (
              <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => onUpdate(request.id, { status: 'submitted' })}>
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Deliverable Item Component
const DeliverableItem = ({ deliverable, onApprove, onRequestRevision }) => {
  const status = deliverableStatusConfig[deliverable.status] || deliverableStatusConfig.pending;
  
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50">
      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
        <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium dark:text-white">{deliverable.title}</h4>
          <Badge className={status.color}>{status.label}</Badge>
        </div>
        <p className="text-sm text-muted-foreground dark:text-slate-400">
          {deliverable.deliverable_type} • {deliverable.delivered_at ? new Date(deliverable.delivered_at).toLocaleDateString() : 'Pending'}
        </p>
        {deliverable.external_link && (
          <a href={deliverable.external_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline flex items-center gap-1 mt-1">
            <ExternalLink className="h-3 w-3" /> View Link
          </a>
        )}
      </div>
      {deliverable.status === 'delivered' && (
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => onRequestRevision(deliverable)}>
            Request Revision
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onApprove(deliverable)}>
            <CheckCircle className="h-4 w-4 mr-1" /> Approve
          </Button>
        </div>
      )}
    </div>
  );
};

// Create Request Dialog
const CreateRequestDialog = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    request_type: 'creative',
    title: '',
    brief: '',
    priority: 'normal',
    due_date: ''
  });

  const handleSave = () => {
    if (!formData.title || !formData.brief) {
      toast.error('Title and brief are required');
      return;
    }
    onSave(formData);
    setFormData({ request_type: 'creative', title: '', brief: '', priority: 'normal', due_date: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg dark:bg-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Building2 className="h-5 w-5 text-emerald-500" />
            New Agency Request
          </DialogTitle>
          <DialogDescription className="dark:text-slate-400">
            Submit a request to So Shall We agency
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="dark:text-slate-200">Request Type</Label>
            <Select value={formData.request_type} onValueChange={(v) => setFormData({ ...formData, request_type: v })}>
              <SelectTrigger className="dark:bg-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="creative">Creative Design</SelectItem>
                <SelectItem value="cro">CRO Audit</SelectItem>
                <SelectItem value="ad_audit">Ad Audit</SelectItem>
                <SelectItem value="landing_page">Landing Page</SelectItem>
                <SelectItem value="reporting">Reporting</SelectItem>
                <SelectItem value="social_management">Social Management</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="dark:text-slate-200">Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief title for the request"
              className="dark:bg-slate-700"
            />
          </div>

          <div>
            <Label className="dark:text-slate-200">Brief</Label>
            <Textarea
              value={formData.brief}
              onChange={(e) => setFormData({ ...formData, brief: e.target.value })}
              placeholder="Describe what you need in detail..."
              rows={4}
              className="dark:bg-slate-700"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="dark:text-slate-200">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="dark:text-slate-200">Due Date</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="dark:bg-slate-700"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
            Create Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Request Detail Dialog
const RequestDetailDialog = ({ request, open, onClose, deliverables, onApproveDeliverable, onRequestRevision }) => {
  if (!request) return null;
  const status = statusConfig[request.status] || statusConfig.draft;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto dark:bg-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            {request.title}
            <Badge className={status.color}>{status.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-sm text-muted-foreground dark:text-slate-400">Type</Label>
            <p className="dark:text-white">{requestTypeLabels[request.request_type]}</p>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground dark:text-slate-400">Brief</Label>
            <p className="dark:text-slate-300 whitespace-pre-wrap">{request.brief}</p>
          </div>

          {request.due_date && (
            <div>
              <Label className="text-sm text-muted-foreground dark:text-slate-400">Due Date</Label>
              <p className="dark:text-white">{new Date(request.due_date).toLocaleDateString()}</p>
            </div>
          )}

          <div className="pt-4 border-t dark:border-slate-700">
            <h4 className="font-medium dark:text-white mb-3">Deliverables</h4>
            {deliverables.length > 0 ? (
              <div className="space-y-2">
                {deliverables.map(d => (
                  <DeliverableItem
                    key={d.id}
                    deliverable={d}
                    onApprove={onApproveDeliverable}
                    onRequestRevision={onRequestRevision}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground dark:text-slate-400">No deliverables yet</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Agency = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deliverables, setDeliverables] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [requestsRes, connectionsRes] = await Promise.all([
        axios.get(`${API}/marketing/agency/requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/agency/connections`, { headers }).catch(() => ({ data: [] }))
      ]);

      setRequests(requestsRes.data || []);
      setConnections(connectionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch agency data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createRequest = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/agency/requests`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request created!');
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create request');
    }
  };

  const updateRequest = async (id, data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.patch(`${API}/marketing/agency/requests/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const viewRequest = async (request) => {
    setSelectedRequest(request);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/marketing/agency/requests/${request.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliverables(res.data.deliverables || []);
    } catch (error) {
      setDeliverables([]);
    }
  };

  const approveDeliverable = async (deliverable) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/agency/deliverables/${deliverable.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deliverable approved!');
      viewRequest(selectedRequest);
    } catch (error) {
      toast.error('Failed to approve deliverable');
    }
  };

  const requestRevision = async (deliverable) => {
    const notes = window.prompt('Enter revision notes:');
    if (!notes) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      await axios.post(`${API}/marketing/agency/deliverables/${deliverable.id}/request-revision?revision_notes=${encodeURIComponent(notes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Revision requested!');
      viewRequest(selectedRequest);
    } catch (error) {
      toast.error('Failed to request revision');
    }
  };

  const openRequests = requests.filter(r => ['submitted', 'in_progress', 'review'].includes(r.status));
  const completedRequests = requests.filter(r => r.status === 'completed');

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <Building2 className="h-8 w-8 text-emerald-500" />
              Agency (So Shall We)
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Manage agency requests and deliverables
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="text-2xl font-bold dark:text-white">{openRequests.length}</div>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Open Requests</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="text-2xl font-bold dark:text-white">{completedRequests.length}</div>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Completed</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="text-2xl font-bold dark:text-white">{requests.filter(r => r.status === 'draft').length}</div>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Drafts</p>
            </CardContent>
          </Card>
          <Card className="dark:bg-slate-800/60">
            <CardContent className="p-4">
              <div className="text-2xl font-bold dark:text-white">{connections.length}</div>
              <p className="text-sm text-muted-foreground dark:text-slate-400">Connections</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="dark:bg-slate-800">
            <TabsTrigger value="requests" className="gap-2">
              <FileText className="h-4 w-4" /> Requests
            </TabsTrigger>
            <TabsTrigger value="connections" className="gap-2">
              <Link2 className="h-4 w-4" /> Connections
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : requests.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {requests.map(request => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    onView={viewRequest}
                    onUpdate={updateRequest}
                  />
                ))}
              </div>
            ) : (
              <Card className="dark:bg-slate-800/60">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Building2 className="h-16 w-16 text-slate-300 dark:text-slate-600 mb-4" />
                  <h3 className="text-lg font-medium dark:text-white">No agency requests yet</h3>
                  <p className="text-muted-foreground dark:text-slate-400 mt-1">
                    Create your first request to get started
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" /> New Request
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="connections" className="mt-6">
            <Card className="dark:bg-slate-800/60">
              <CardHeader>
                <CardTitle className="dark:text-white">Agency Connections</CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Configure connections to external agencies
                </CardDescription>
              </CardHeader>
              <CardContent>
                {connections.length > 0 ? (
                  <div className="space-y-3">
                    {connections.map(conn => (
                      <div key={conn.id} className="flex items-center justify-between p-4 rounded-lg border dark:border-slate-700">
                        <div>
                          <h4 className="font-medium dark:text-white">{conn.display_name || conn.provider}</h4>
                          <p className="text-sm text-muted-foreground dark:text-slate-400">Provider: {conn.provider}</p>
                        </div>
                        <Badge className={conn.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {conn.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p className="text-muted-foreground dark:text-slate-400">
                      No agency connections configured
                    </p>
                    <p className="text-sm text-muted-foreground dark:text-slate-500 mt-1">
                      Configure connections in Marketing Settings
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <CreateRequestDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSave={createRequest}
        />

        <RequestDetailDialog
          request={selectedRequest}
          open={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
          deliverables={deliverables}
          onApproveDeliverable={approveDeliverable}
          onRequestRevision={requestRevision}
        />
      </div>
    </Layout>
  );
};
